import React, { useState } from 'react';
import ContainerHeader from '../components/ContainerHeader';
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Collapse,
  Menu,
  InputAdornment,
  Divider,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useDocket, { FILTER_DEFINITIONS, INITIAL_FILTER } from './CustomHooks/useDocket';
import DocketInfoDialog from './components/docketInfoDialog';
import EditSectionModal from './components/EditTableDataGenericModal';
import DocketDocsDialog from './components/DocketDocsDialog';
import NotificationConfirmDialog from './components/DocketNotificationConfirmation';
import customAxios from '../util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useSendContractNotification } from '../TimberSalesPricingList/CustomHooks/useSendContractNotification';

export class DebugBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught error:', error, info);
  }
  render() {
    if (this.state.hasError) return <div>Error in grid rendering</div>;
    return this.props.children;
  }
}

// ─── Input config (unchanged) ─────────────────────────────────────────────────

const transfersInputKeys = [
  {
    name: 'submissionDate',
    typeOfInp: 'input',
    label: 'Submission Date',
    textFieldType: 'datetime-local',
    width: 3,
    groupLabel: 'Transfer To',
  },
  { name: 'transferToName', typeOfInp: 'select', label: 'Transfer To Name', allowCustomInput: true, width: 3 },
  { name: 'transferToEmail', typeOfInp: 'select', label: 'Transfer To Email', allowCustomInput: true, width: 3 },
  { name: 'transferToTruck', typeOfInp: 'select', label: 'Transfer To Truck', allowCustomInput: true, width: 3 },
  { name: 'transferToTrailer', typeOfInp: 'select', label: 'Transfer To Trailer', allowCustomInput: true, width: 3 },
  { name: 'driverName', typeOfInp: 'select', label: 'Driver Name', allowCustomInput: true, width: 3, groupLabel: 'Driver' },
  { name: 'driverEmail', typeOfInp: 'select', label: 'Driver Email', allowCustomInput: true, width: 3 },
  {
    name: 'unloadingTruck',
    typeOfInp: 'select',
    label: 'Unloading Truck',
    allowCustomInput: true,
    width: 3,
    groupLabel: 'Unloading',
  },
  { name: 'unloadingTrailer', typeOfInp: 'select', label: 'Unloading Trailer', allowCustomInput: true, width: 3 },
  {
    name: 'distanceFromPickuptoDropoffPoint',
    typeOfInp: 'input',
    label: 'Distance (Pickup → Dropoff)',
    textFieldType: 'number',
    width: 3,
  },
  {
    name: 'transferInformation',
    typeOfInp: 'input',
    label: 'Transfer Information',
    textFieldType: 'text',
    isTextArea: true,
    width: 12,
  },
];
const journeyDetailsInputKeys = [
  {
    name: 'submissionDate',
    typeOfInp: 'input',
    label: 'Submission Date',
    textFieldType: 'datetime-local',
    width: 3,
    groupLabel: 'Journey Stats',
  },
  { name: 'journeyDurationMins', typeOfInp: 'input', label: 'Duration (mins)', textFieldType: 'number', width: 3 },
  { name: 'journeyDistanceKm', typeOfInp: 'input', label: 'Distance (km)', textFieldType: 'number', width: 3 },
  { name: 'journeyFuelLit', typeOfInp: 'input', label: 'Fuel (litres)', textFieldType: 'number', width: 3 },
  { name: 'journeyLoadTonne', typeOfInp: 'input', label: 'Load (tonnes)', textFieldType: 'number', width: 3 },
  { name: 'job', typeOfInp: 'input', label: 'Job', textFieldType: 'text', width: 3 },
  { name: 'name2', typeOfInp: 'input', label: 'Name 2', textFieldType: 'text', width: 3 },
  { name: 'address', typeOfInp: 'input', label: 'Address', textFieldType: 'text', isTextArea: true, width: 8 },
  {
    name: 'startTimeOnSite',
    typeOfInp: 'input',
    label: 'Start Time on Site',
    textFieldType: 'text',
    width: 3,
    groupLabel: 'Times & Loads',
  },
  { name: 'finishTimeOnSite', typeOfInp: 'input', label: 'Finish Time on Site', textFieldType: 'number', width: 3 },
  { name: 'jobStartandEndTime', typeOfInp: 'input', label: 'Job Start & End Time', textFieldType: 'text', width: 3 },
  { name: 'chippingLoads', typeOfInp: 'input', label: 'Chipping Loads', textFieldType: 'text', width: 3 },
  { name: 'loads', typeOfInp: 'input', label: 'Loads', textFieldType: 'text', width: 3 },
  { name: 'totalVolume', typeOfInp: 'input', label: 'Total Volume', textFieldType: 'number', width: 3 },
  {
    name: 'chargeableTravelTimetoJob',
    typeOfInp: 'input',
    label: 'Chargeable Travel To Job',
    textFieldType: 'number',
    width: 3,
  },
  { name: 'endKm', typeOfInp: 'input', label: 'End KM', textFieldType: 'number', width: 3, groupLabel: 'KM & Charges' },
  { name: 'totalKM', typeOfInp: 'input', label: 'Total KM', textFieldType: 'number', width: 3 },
  { name: 'nonChargeableTime', typeOfInp: 'input', label: 'Non-Chargeable Time', textFieldType: 'number', width: 3 },
  {
    name: 'chargableTravelTimeFromJob',
    typeOfInp: 'input',
    label: 'Chargeable Travel From Job',
    textFieldType: 'number',
    width: 3,
  },
  { name: 'chargeableTime', typeOfInp: 'input', label: 'Chargeable Time', textFieldType: 'number', width: 3 },
  { name: 'totalChargeableTime', typeOfInp: 'input', label: 'Total Chargeable Time', textFieldType: 'number', width: 3 },
];
const weighbridgeInputKeys = [
  {
    name: 'submissionDate',
    typeOfInp: 'input',
    label: 'Submission Date',
    textFieldType: 'datetime-local',
    width: 3,
    groupLabel: 'Weighbridge Details',
  },
  { name: 'customer', typeOfInp: 'select', label: 'Customer', width: 3 },
  { name: 'weightBridge', typeOfInp: 'select', label: 'Weight Bridge', width: 3 },
  {
    name: 'weightbridgeDocketNumber',
    typeOfInp: 'input',
    label: 'Weightbridge Docket Number',
    textFieldType: 'text',
    width: 3,
  },
  {
    name: 'unloadingDetailsGrWgt',
    typeOfInp: 'input',
    label: 'Gross Weight',
    textFieldType: 'number',
    width: 3,
    groupLabel: 'Weights',
  },
  { name: 'unloadingDetailsNetWgt', typeOfInp: 'input', label: 'Net Weight', textFieldType: 'number', width: 3 },
  { name: 'wB_Docket', typeOfInp: 'input', label: 'WB Docket', textFieldType: 'text', width: 8, groupLabel: 'Additional' },
  {
    name: 'additionalInformation',
    typeOfInp: 'input',
    label: 'Additional Information',
    textFieldType: 'text',
    isTextArea: true,
    width: 12,
  },
];
const loadingDetailsInputKeys = [
  {
    name: 'docketId',
    typeOfInp: 'input',
    label: 'Docket Id',
    textFieldType: 'text',
    readOnly: true,
    width: 3,
    groupLabel: 'Docket & Product Info',
  },
  {
    name: 'dateAndTime',
    typeOfInp: 'input',
    label: 'Date and Time',
    textFieldType: 'datetime-local',
    width: 3,
  },
  { name: 'spNumber', typeOfInp: 'select', label: 'SP Number', width: 3 },
  { name: 'docketNumber', typeOfInp: 'input', label: 'WH Docket Number', textFieldType: 'text', width: 3 },
  { name: 'productType', typeOfInp: 'select', label: 'Product Type', width: 3 },
  { name: 'timberProductUsedForWoodchip', typeOfInp: 'select', label: 'Product', width: 3 },
  { name: 'name', typeOfInp: 'select', label: 'Destination', width: 3 },
  { name: 'isLastLoad', typeOfInp: 'select', label: 'Is Last Full Load', width: 3 },
  { name: 'truck', typeOfInp: 'select', label: 'Truck', width: 3, isFreeText: true, groupLabel: 'Vehicle Info' },
  { name: 'trailer', typeOfInp: 'select', label: 'Trailer', width: 3, isFreeText: true },
  { name: 'driverName', typeOfInp: 'select', label: 'Driver', width: 3, groupLabel: 'Driver & Loader' },
  { name: 'driverUserName', typeOfInp: 'select', label: 'Entered By', width: 3 },
  { name: 'loadedBy', typeOfInp: 'select', label: 'Loaded By', width: 3, isFreeText: true },
  { name: 'loaderMachineOrTruck', typeOfInp: 'select', label: 'Loader Machine', width: 3, isFreeText: true },
  {
    name: 'supplierWeighbridgeGrWgt',
    typeOfInp: 'input',
    label: 'Supplier Gross Wt',
    textFieldType: 'text',
    width: 3,
    groupLabel: 'Supplier & Permit Info',
  },
  { name: 'supplierWeighbridgeNetWgt', typeOfInp: 'input', label: 'Supplier Net Wt', textFieldType: 'text', width: 3 },
  { name: 'supplierDocketNumber', typeOfInp: 'input', label: 'Supplier Docket Number', textFieldType: 'text', width: 3 },
  { name: 'permitNumber', typeOfInp: 'input', label: 'Permit Number', textFieldType: 'text', width: 3 },
  { name: 'wH_Docket', typeOfInp: 'input', label: 'WH Docket', textFieldType: 'text', width: 8, hidden: true },
  { name: 'permit', typeOfInp: 'input', label: 'Permit', textFieldType: 'doc', width: 3, hidden: true },
  { name: 'supplierDocket', typeOfInp: 'input', label: 'Supplier Docket', textFieldType: 'text', width: 12, hidden: true },
  {
    name: 'internalNoteforWH',
    typeOfInp: 'input',
    label: 'Internal Notes',
    textFieldType: 'text',
    isTextArea: true,
    width: 12,
  },
];

const getInputArrayList = sectionName => {
  if (sectionName === 'journeyDetails') return journeyDetailsInputKeys;
  if (sectionName === 'transfers') return transfersInputKeys;
  if (sectionName === 'weighbridge') return weighbridgeInputKeys;
  if (sectionName === 'loadingDetails') return loadingDetailsInputKeys;
  return [];
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = val => {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const StatusChip = ({ row }) => {
  if (row.cancelLoad === 'Yes')
    return <Chip label="Cancelled" size="small" color="error" variant="outlined" sx={{ fontSize: 11 }} />;
  if (row.isDocketOpen === 'OPEN')
    return <Chip label="Open" size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />;
  return null;
};

// ─── Column definitions ───────────────────────────────────────────────────────

const COLUMNS = [
  { id: 'status', label: 'Status', width: 90 },
  { id: 'docketNumber', label: 'Docket ID', width: 100, isLink: true },
  { id: 'driverUserName', label: 'Entered By', width: 110 },
  { id: 'driverName', label: 'Driver', width: 130, isDriver: true },
  { id: 'loaderDriver', label: 'Loaded By', width: 100 },
  { id: 'submissionDate', label: 'Sub Date', width: 100, isDate: true },
  { id: 'dateAndTime', label: 'Date & Time', width: 100, isDate: true },
  { id: 'forestSP', label: 'SP', width: 100 },
  { id: 'truck', label: 'Truck', width: 90 },
  { id: 'trailer', label: 'Trailer', width: 140 },
  { id: 'productType', label: 'Product Type', width: 120 },
  { id: 'timberProductUsedForWoodchip', label: 'Product', width: 110 },
  { id: 'internalNoteforWH', label: 'Internal WH Notes', width: 200, isTextArea: true },
  { id: 'name', label: 'Destination', width: 130 },
  { id: 'isLastLoad', label: 'Is Last Full Load', width: 90 },
  { id: 'weightbridgeDocketNumber', label: 'WB Docket', width: 110 },
  { id: 'unloadingDetailsGrWgt', label: 'Gross Wt', width: 90 },
  { id: 'unloadingDetailsNetWgt', label: 'Net Wt', width: 90 },
];

// ─── Row context menu ─────────────────────────────────────────────────────────

function RowMenu({ row, onCancelLoad, sendNotification }) {
  const [anchor, setAnchor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, recipients: [] });

  const handleSendNotification = async (type, forestSP) => {
    console.log('forestSP ===', forestSP);

    if (forestSP) {
      const response = await customAxios.get(`/Ops/GetContractsUsersToNotify?contractUniqueID=${forestSP}`, {
        headers: authHeader(),
      });

      console.log('response ===', response);
      // Determine which notificationSection(s) to filter by
      const sectionMap = {
        load: ['DocketLoad'],
        unload: ['DocketUnloading'],
      };
      const relevantSections = sectionMap[type] ?? [];

      const filtered = (response?.data ?? []).filter(item => relevantSections.includes(item.notificationSection));

      // Deduplicate by emailOrPhone — if same address appears as both email+sms keep both,
      // but collapse exact duplicates (same address + same type)
      const seen = new Set();
      const recipients = filtered.reduce((acc, item) => {
        const key = `${item.notificationType}::${item.emailOrPhone}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push(item);
        }
        return acc;
      }, []);

      setAnchor(null);
      setConfirmDialog({ open: true, type, recipients });
    }
  };

  const handleConfirm = async () => {
    if (confirmDialog.recipients.length > 0) {
      const sendTo = confirmDialog.recipients.map(r => r.emailOrPhone).join(',');

      if (confirmDialog.type === 'load') {
        await sendNotification({
          sendTo,
          subject: `Docket Loading - ${row.docketNumber} | ${row?.forestSP}`,
          body: null,
          deliveryType: 'EMAIL',
          primaryKey: `${row.docketNumber}`,
          templateType: 'docketloading',
          notificationSection: 'docketloading',
        });
      } else if (confirmDialog.type === 'unload') {
        await sendNotification({
          sendTo,
          subject: `Docket Unloading - ${row.docketNumber} | ${row?.forestSP}`,
          body: null,
          deliveryType: 'EMAIL',
          primaryKey: `${row.docketNumber}`,
          templateType: 'docketunloading',
          notificationSection: 'docketunloading',
        });
      }
    }

    setConfirmDialog({ open: false, type: null, recipients: [] });
  };

  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ p: '2px' }}>
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem dense onClick={() => handleSendNotification('load', row?.forestSP)} disabled={row.cancelLoad === 'Yes'}>
          Send Load Notification
        </MenuItem>
        <MenuItem dense onClick={() => handleSendNotification('unload', row?.forestSP)} disabled={row.cancelLoad === 'Yes'}>
          Send Unload Notification
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            onCancelLoad(row);
            setAnchor(null);
          }}
          disabled={row.cancelLoad === 'Yes'}>
          Cancel Load
        </MenuItem>
      </Menu>

      <NotificationConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, recipients: [] })}
        onConfirm={handleConfirm}
        type={confirmDialog.type}
        recipients={confirmDialog.recipients ?? []}
      />
    </>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────────────────
// Uses GetDistinctFilterValues dropdowns for the 5 column filters.
// Search fields map to docketNumber / username params.

function FilterBar({ activeFilter, onApply, onClear, distinctValues, distinctLoading, fetchDistinctValues }) {
  const [draft, setDraft] = useState(activeFilter);

  // Keep draft in sync when parent clears
  React.useEffect(() => {
    setDraft(activeFilter);
  }, [activeFilter]);

  const setCol = (filterColumn, filterValue) => setDraft(prev => ({ ...prev, filterColumn, filterValue }));

  const setSearch = (field, val) => setDraft(prev => ({ ...prev, [field]: val }));

  const handleColumnChange = (key, value) => {
    const def = FILTER_DEFINITIONS.find(f => f.key === key);
    if (!def) return;
    setCol(value ? def.apiColumn : '', value);
  };

  // When a dropdown opens, lazily fetch its distinct values
  const handleDropdownOpen = key => fetchDistinctValues(key);

  const hasAnyFilter = draft.filterColumn || draft.filterValue || draft.searchDocketNumber || draft.searchUsername;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5, py: 1.5 }}>
      {/* Search by Docket # */}
      <TextField
        size="small"
        label="Docket #"
        sx={{ width: 130 }}
        value={draft.searchDocketNumber}
        onChange={e => setSearch('searchDocketNumber', e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onApply(draft)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Search by Username */}
      <TextField
        size="small"
        label="Username"
        sx={{ width: 150 }}
        value={draft.searchUsername}
        onChange={e => setSearch('searchUsername', e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onApply(draft)}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* One dropdown per FILTER_DEFINITION */}
      {FILTER_DEFINITIONS.map(def => {
        const isActiveCol = draft.filterColumn === def.apiColumn;
        const options = distinctValues[def.key] ?? [];
        const loading = distinctLoading[def.key];

        return (
          <TextField
            key={def.key}
            select
            size="small"
            label={def.label}
            sx={{ minWidth: 160 }}
            value={isActiveCol ? draft.filterValue ?? '' : ''}
            SelectProps={{
              onOpen: () => handleDropdownOpen(def.key),
              displayEmpty: true,
            }}
            InputLabelProps={{ shrink: true }}
            onChange={e => handleColumnChange(def.key, e.target.value)}>
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {loading && <MenuItem disabled>Loading…</MenuItem>}
            {options.map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        );
      })}

      {/* Action buttons */}
      <Button variant="contained" size="small" sx={{ height: 40 }} onClick={() => onApply(draft)}>
        Apply
      </Button>
      {hasAnyFilter && (
        <Button
          variant="outlined"
          size="small"
          color="inherit"
          sx={{ height: 40 }}
          startIcon={<ClearIcon />}
          onClick={() => {
            setDraft(INITIAL_FILTER);
            onClear();
          }}>
          Clear
        </Button>
      )}
    </Box>
  );
}

// ─── Active filter summary chips ──────────────────────────────────────────────

function ActiveFilterChips({ activeFilter, onClear }) {
  const chips = [];
  if (activeFilter.filterColumn && activeFilter.filterValue) {
    const def = FILTER_DEFINITIONS.find(f => f.apiColumn === activeFilter.filterColumn);
    chips.push(`${def?.label ?? activeFilter.filterColumn}: ${activeFilter.filterValue}`);
  }
  if (activeFilter.searchDocketNumber) chips.push(`Docket #: ${activeFilter.searchDocketNumber}`);
  if (activeFilter.searchUsername) chips.push(`Username: ${activeFilter.searchUsername}`);

  if (!chips.length) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pb: 1, alignItems: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Active:
      </Typography>
      {chips.map(c => (
        <Chip key={c} label={c} size="small" onDelete={onClear} color="primary" variant="outlined" sx={{ fontSize: 11 }} />
      ))}
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function a11yProps(i) {
  return { id: `tab-${i}`, 'aria-controls': `tabpanel-${i}` };
}

const DocketsPage = props => {
  const { sendNotification } = useSendContractNotification();
  const [showFilters, setShowFilters] = useState(true);

  const {
    docketData,
    totalDocketCount,
    openDocketData,
    tabValue,
    handleTabChange,
    handleOpenDocketInfoModal,
    handleCloseDocketInfoModal,
    handleDocketInfoEditStart,
    openDocketInfoModal,
    setOpenDocketInfoModal,
    docketInfoData,
    selectedDocket,
    handleDocketSectionEdit,
    selectedDocketSection,
    openDocketEditModal,
    setOpenDocketEditModal,
    files,
    handleDocketDocs,
    isOpen,
    handleFileChange,
    handleDeleteFiles,
    viewFile,
    docType,
    gridRef,
    isDocketDataLoading,
    allUsers,
    cancelLoad,
    handleGridPageChange,
    pageSize,
    currentPage,
    activeFilter,
    handleApplyFilter,
    handleClearFilter,
    distinctValues,
    distinctLoading,
    fetchDistinctValues,
    handleExportToExcel,
    handleDocketInfoEditClose,
  } = useDocket();

  console.log('DISTINCT VALUES:', distinctValues, 'LOADING:', distinctLoading);

  const hasActiveFilter =
    activeFilter.filterColumn || activeFilter.filterValue || activeFilter.searchDocketNumber || activeFilter.searchUsername;

  const driverDisplay = email => {
    if (!email) return '—';
    return (allUsers && allUsers[email.toLowerCase()]) || email;
  };

  // ── Shared table ──────────────────────────────────────────────────────────

  const renderTable = (rows, withPager) => (
    <TableContainer
      component={Paper}
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 1,
        height: withPager ? 'calc(100vh - 300px)' : 'calc(100vh - 250px)',
        overflow: 'auto',
      }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1700 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 36, p: '4px 6px', bgcolor: 'grey.50' }} />
            {COLUMNS.map(col => (
              <TableCell
                key={col.id}
                sx={{
                  width: col.width,
                  minWidth: col.width,
                  fontWeight: 700,
                  bgcolor: 'grey.50',
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  p: '6px 8px',
                }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                {isDocketDataLoading ? 'Loading…' : 'No records found'}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => (
              <TableRow key={row.docketNumber ?? idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ p: '2px 4px' }}>
                  <RowMenu row={row} onCancelLoad={cancelLoad} sendNotification={sendNotification} />
                </TableCell>
                {COLUMNS.map(col => {
                  let content;
                  if (col.id === 'status') {
                    content = <StatusChip row={row} />;
                  } else if (col.isLink) {
                    content = (
                      <Button
                        size="small"
                        onClick={() => handleOpenDocketInfoModal(row)}
                        sx={{
                          color: 'primary.main',
                          fontWeight: 600,
                          p: 0,
                          minWidth: 0,
                          textTransform: 'none',
                          fontSize: 12,
                        }}>
                        {row[col.id]}
                      </Button>
                    );
                  } else if (col.isDriver) {
                    content = driverDisplay(row[col.id]);
                  } else if (col.isDate) {
                    content = formatDate(row[col.id]);
                  } else {
                    content = row[col.id] ?? '—';
                  }

                  if (col.isTextArea) {
                    return (
                      <TableCell key={col.id} sx={{ fontSize: 12, maxWidth: col.width, p: '6px 8px' }}>
                        {content}
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={col.id}
                      sx={{
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: col.width ?? 120,
                        p: '6px 8px',
                      }}>
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="app-wrapper siteWrapper">
      <ContainerHeader match={props.match} title="Dockets" />

      {/* Tabs + toolbar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="dockets tabs">
          <Tab label={`Open (${openDocketData.length})`} {...a11yProps(0)} />
          <Tab label={`All (${totalDocketCount || docketData.length})`} {...a11yProps(1)} />
        </Tabs>

        {tabValue === 1 && (
          <Box sx={{ display: 'flex', gap: 0.5, pr: 1 }}>
            <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
              <IconButton
                size="small"
                onClick={() => setShowFilters(v => !v)}
                color={hasActiveFilter ? 'primary' : 'default'}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export all matching records to CSV">
              <IconButton size="small" onClick={handleExportToExcel}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Filter bar (All tab only) */}
      {tabValue === 1 && (
        <Collapse in={showFilters}>
          <Box sx={{ px: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            <FilterBar
              activeFilter={activeFilter}
              onApply={handleApplyFilter}
              onClear={handleClearFilter}
              distinctValues={distinctValues}
              distinctLoading={distinctLoading}
              fetchDistinctValues={fetchDistinctValues}
            />
          </Box>
        </Collapse>
      )}

      {/* Active filter chips */}
      {tabValue === 1 && hasActiveFilter && (
        <Box sx={{ pt: 1 }}>
          <ActiveFilterChips activeFilter={activeFilter} onClear={handleClearFilter} />
        </Box>
      )}

      {/* Grid */}
      <Box sx={{ width: '100%', position: 'relative', mt: 1 }}>
        {isDocketDataLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.65)',
            }}>
            <CircularProgress />
          </Box>
        )}
        {tabValue === 0 ? renderTable(openDocketData, false) : renderTable(docketData, true)}
      </Box>

      {/* Pagination (All tab) */}
      {tabValue === 1 && (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Pagination
            count={Math.max(1, Math.ceil(totalDocketCount / pageSize))}
            shape="rounded"
            page={currentPage}
            color="primary"
            showFirstButton
            showLastButton
            size="small"
            onChange={(_, p) => handleGridPageChange({ currentPage: p, pageSize })}
          />
          <Typography variant="caption" color="text.secondary">
            {totalDocketCount.toLocaleString()} records · page {currentPage} of{' '}
            {Math.max(1, Math.ceil(totalDocketCount / pageSize))} · {pageSize}/page
          </Typography>
        </Box>
      )}

      {/* Dialogs */}
      <DocketInfoDialog
        open={openDocketInfoModal}
        handleCloseDocketInfoModal={handleCloseDocketInfoModal}
        docketInfoData={docketInfoData}
        docketMainData={selectedDocket}
        handleDocketInfoEdit={handleDocketInfoEditStart}
        handleDocketDocs={handleDocketDocs}
      />
      <EditSectionModal
        open={openDocketEditModal}
        handleEditClose={handleDocketInfoEditClose}
        getInputArrayList={getInputArrayList}
        sectionData={selectedDocketSection.sectionData}
        sectionName={selectedDocketSection.sectionName}
        handleEditSave={handleDocketSectionEdit}
        handleDocketDocs={handleDocketDocs}
      />
      <DocketDocsDialog
        docsDialogOpen={isOpen}
        handleDocsDialog={handleDocketDocs}
        handleFileChange={handleFileChange}
        handleDeleteFiles={handleDeleteFiles}
        viewFile={viewFile}
        files={files}
        docType={docType}
      />
    </div>
  );
};

export default DocketsPage;
